import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { socketSMSRateLimits } from 'src/actions/accountActions';
import { useWebSocket } from 'src/context/WebSocketContext';
import { SOCKET_EVENTS } from 'src/constants/socket';
import Box from '@mui/material/Box';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import ProgressBar from 'src/components/ProgressBar';
import Tooltip from 'src/components/Tooltip';
import helpers from 'src/helpers';
import {
  FONT_FAMILY,
  COLOR_BLUE,
  COLOR_BODY,
  COLOR_BLUE_BAYOUX,
} from 'src/constants/styleOptions';

const COLOR_GREEN = '#57B894';
const COLOR_YELLOW = '#FFD944';
const COLOR_RED = '#F16565';

function DailyTextUsage({
  dailyLimitCount = 0,
  textsUsedCount = 0,
}) {
  const dispatch = useDispatch();
  const socketService = useWebSocket();

  const textsUsedPercentages = useMemo(() => {
    const dailyLimitNumber = helpers.transform.toInt10(dailyLimitCount);
    const textsUsedNumber = helpers.transform.toInt10(textsUsedCount);
    const resultPercentages = !dailyLimitNumber || !textsUsedNumber ? 0 : Math.round((textsUsedNumber / dailyLimitNumber) * 100);

    return resultPercentages > 100 ? 100 : resultPercentages;
  }, [textsUsedCount, dailyLimitCount]);

  const progressBarColor = useMemo(() => {
    if (textsUsedPercentages < 61) {
      return COLOR_GREEN;
    }

    return textsUsedPercentages < 91 ? COLOR_YELLOW : COLOR_RED;
  }, [textsUsedPercentages]);

  const handleRateLimits = (rateLimits) => {
    dispatch(socketSMSRateLimits(rateLimits));
  };

  useEffect(() => {
    if (socketService) {
      socketService.on(SOCKET_EVENTS.RATE_LIMITS, handleRateLimits);
    }

    return () => {
      if (socketService) {
        socketService.off(SOCKET_EVENTS.RATE_LIMITS, handleRateLimits);
      }
    };
  }, [socketService]);

  return (
    <Box
      className="dailyTextUsage"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '202px',
        padding: '5px 24px',
        marginBottom: '4px',
        boxSizing: 'border-box',
        border: '1px solid #5850EC',
        borderRadius: '6px',
      }}
    >
      <Box
        className="dailyTextUsageInfo"
        sx={{
          position: 'absolute',
          top: '6px',
          left: '5px',
        }}
      >
        <Tooltip
          title={`Your current daily limit is ${helpers.transform.toInt10(dailyLimitCount).toLocaleString('en-US')} texts. This resets at midnight Pacific Time.`}
        >
          <InfoOutlinedIcon
            sx={{
              color: COLOR_BLUE,
              fontSize: '16px',
            }}
          />
        </Tooltip>
      </Box>
      <div className="dailyTextUsageText">
        <Box
          sx={{
            fontSize: '11px',
            color: COLOR_BODY,
            lineHeight: '1.5em',
            fontWeight: 600,
            fontFamily: FONT_FAMILY,
            paddingTop: '2px',
          }}
        >
          Daily Text Usage
        </Box>
        <ProgressBar
          currentValuePercentages={textsUsedPercentages}
          containerOptions={{
            paddingTop: '4px',
            paddingBottom: '4px',
          }}
          colorBar={progressBarColor}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            width: '100%',
            fontSize: '12px',
            color: COLOR_BLUE_BAYOUX,
            lineHeight: '1.5em',
            fontStyle: 'italic',
            fontFamily: FONT_FAMILY,
          }}
        >
          {textsUsedPercentages > 90 && (
            <WarningIcon
              sx={{
                color: COLOR_RED,
                fontSize: '16px',
              }}
            />
          )}
          <Box
            sx={{
              padding: '0px 0px 0px 2px',
              whiteSpace: 'normal',
            }}
          >
            {`${helpers.transform.toInt10(textsUsedCount).toLocaleString('en-US')} of ${helpers.transform.toInt10(dailyLimitCount).toLocaleString('en-US')} texts used`}
          </Box>
        </Box>
      </div>
    </Box>
  );
}

DailyTextUsage.propTypes = {
  dailyLimitCount: PropTypes.number,
  textsUsedCount: PropTypes.number,
};

export default DailyTextUsage;
